import { Navigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { portalApi } from '@/api/endpoints/portal.api';
import { portalStorage } from '@/lib/secureStorage';

interface PortalRouteProps {
  children: React.ReactNode;
}

export function PortalRoute({ children }: PortalRouteProps) {
  const location = useLocation();
  const [isValidating, setIsValidating] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const validateSession = async () => {
      const portalToken = portalStorage.getToken();

      if (!portalToken) {
        setIsAuthenticated(false);
        setIsValidating(false);
        return;
      }

      // Check if token is expired locally first
      if (portalStorage.isExpired()) {
        // Token expired, clear storage
        portalStorage.clear();
        setIsAuthenticated(false);
        setIsValidating(false);
        return;
      }

      // Validate session with backend
      try {
        await portalApi.validateSession();
        setIsAuthenticated(true);
      } catch {
        // Session invalid, clear storage
        portalStorage.clear();
        setIsAuthenticated(false);
      } finally {
        setIsValidating(false);
      }
    };

    validateSession();
  }, []);

  if (isValidating) {
    return <LoadingSpinner fullScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/portal/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
